import api from './api';

/**
 * Company (tenant) administration.
 *
 * Platform-owner calls and company-admin calls hit the same controller; the
 * server decides what each caller may see. Nothing here needs to know the
 * caller's role, so a mistake in the UI cannot widen access.
 */
export const companyService = {
  // ── Platform console (platform owner) ──────────────────────────────────
  getAll: async () => (await api.get('/clinics/all')).data,
  getUsage: async () => (await api.get('/clinics/usage')).data,

  /** Creates a company and its first admin in one call. */
  provision: async (payload) => (await api.post('/clinics/provision', payload)).data,

  suspend: async (companyId, reason) =>
    (await api.put(`/clinics/${companyId}/suspend`, { reason })).data,

  activate: async (companyId) => (await api.put(`/clinics/${companyId}/activate`)).data,

  /** Permanently removes a company and its private tenant data. */
  deletePermanently: async (companyId) =>
    (await api.delete(`/clinics/${companyId}/permanent`)).data,

  // ── Shared ─────────────────────────────────────────────────────────────
  /** Companies visible to the caller (own company unless platform owner). */
  list: async () => (await api.get('/clinics')).data,
  getById: async (companyId) => (await api.get(`/clinics/${companyId}`)).data,

  /** Applies white-label branding. Only supplied fields are written. */
  updateBranding: async (companyId, payload) =>
    (await api.put(`/clinics/${companyId}/branding`, payload)).data,

  /** Uploads and applies a company logo. */
  uploadLogo: async (companyId, file) => {
    const body = new FormData();
    body.append('file', file);
    return (await api.post(`/clinics/${companyId}/logo`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
};

/**
 * Public branding lookup (no session required).
 * Pass a form-link token so the fill page shows the right company's brand.
 */
export const brandingService = {
  get: async ({ token, company } = {}) => {
    const params = {};
    if (token) params.token = token;
    if (company) params.company = company;
    return (await api.get('/branding', { params })).data;
  },
};

export default companyService;
